import { Flex, Switch } from "antd";
import { useCallback } from "react";
import { Option, Product, SortOrder } from "tm-shared/types";

import BaseButton from "@/components/BaseButton";
import FlexContainer from "@/components/FlexContainer";
import { SelectWithToggle } from "@/components/SelectWithToggle";
import { PrimaryText } from "@/components/Typography/Styled";

import { AutoGeneratedFilters } from "./components/AutoGeneratedFilters";
import { FacetFilterTags } from "./components/FacetFilterTags";
import { StyledDrawer } from "./FilterDrawerUI.styled";

export interface SortConfig {
    sortTag: string | null;
    sortOrder: SortOrder;
    sortOptions: Option[];
    onSortTagChange: (tag: string | null) => void;
    onSortOrderChange: (order: SortOrder) => void;
}

type FilterValue = string | number | boolean;

interface FilterDrawerUIProps<T extends Product = Product> {
    onClose: () => void;
    onApply: () => void;
    onReset: () => void;

    isOpen: boolean;
    products: Record<string, T>;
    availableProductsLength: number;

    availableFilters: Record<string, { title: string; options: FilterValue[] }>;
    selectedFilters: Record<string, FilterValue[]>;
    onFilterChange: (tag: string, values: FilterValue[]) => void;

    hideZeroProducts?: boolean;
    showOnlyMML?: boolean;
    onToggleHideZeroProducts?: () => void;
    onToggleShowOnlyMML?: () => void;

    sortConfig?: {
        sortTag: string | null;
        sortOrder: SortOrder;
        sortOptions: Option[];
        onSortTagChange: (tag: string | null) => void;
        onSortOrderChange: (order: SortOrder) => void;
    };
}

export function FilterDrawerUI<T extends Product = Product>({
    isOpen,
    onClose,
    onApply,
    onReset,
    products,
    availableFilters,
    selectedFilters,
    onFilterChange,
    hideZeroProducts,
    onToggleHideZeroProducts,
    showOnlyMML,
    onToggleShowOnlyMML,
    availableProductsLength,
    sortConfig,
}: FilterDrawerUIProps<T>) {
    const handleRemoveFilter = useCallback(
        (tag: string, value: string | number | boolean) => {
            const currentValues = selectedFilters[tag] || [];
            const updatedValues = currentValues.filter((v) => v !== value);
            onFilterChange(tag, updatedValues);
        },
        [selectedFilters, onFilterChange],
    );

    const extraFilters =
        !!onToggleHideZeroProducts || !!onToggleHideZeroProducts;

    return (
        <StyledDrawer
            title="Фільтр"
            $withoutBorder={availableProductsLength === 0}
            open={isOpen}
            onClose={onClose}
            footer={
                <>
                    {extraFilters && (
                        <FlexContainer
                            justifyContent="flex-start"
                            alignItems="flex-start"
                            gap="15px"
                            flexDirection="column"
                        >
                            {onToggleHideZeroProducts &&
                                typeof hideZeroProducts === "boolean" && (
                                    <FlexContainer
                                        justifyContent="flex-start"
                                        gap="5px"
                                    >
                                        <Switch
                                            checked={hideZeroProducts}
                                            onChange={onToggleHideZeroProducts}
                                        />
                                        <PrimaryText>
                                            Приховати нульові залишки
                                        </PrimaryText>
                                    </FlexContainer>
                                )}

                            {onToggleShowOnlyMML &&
                                typeof showOnlyMML === "boolean" && (
                                    <FlexContainer
                                        justifyContent="flex-start"
                                        gap="5px"
                                    >
                                        <Switch
                                            checked={showOnlyMML}
                                            onChange={onToggleShowOnlyMML}
                                        />
                                        <PrimaryText>
                                            Показати тільки MML
                                        </PrimaryText>
                                    </FlexContainer>
                                )}
                        </FlexContainer>
                    )}
                    <BaseButton
                        type="primary"
                        margin="16px 0"
                        onClick={onApply}
                    >
                        ({availableProductsLength}) Застосувати
                    </BaseButton>

                    <BaseButton type="text" onClick={onReset}>
                        Скинути всі фільтри
                    </BaseButton>
                </>
            }
        >
            <Flex vertical gap={16}>
                {sortConfig && (
                    <SelectWithToggle
                        options={sortConfig.sortOptions}
                        selectValue={sortConfig.sortTag}
                        order={sortConfig.sortOrder}
                        onOrderChange={sortConfig.onSortOrderChange}
                        onChangeSelect={sortConfig.onSortTagChange}
                        selectWidth={"100%"}
                    />
                )}

                <FacetFilterTags
                    products={products}
                    selectedFilters={selectedFilters}
                    onRemoveFilter={handleRemoveFilter}
                />
                <AutoGeneratedFilters
                    availableFilters={availableFilters}
                    selectedFilters={selectedFilters}
                    onFilterChange={onFilterChange}
                />
            </Flex>
        </StyledDrawer>
    );
}
